import React, { useEffect, useMemo, useRef, useState } from 'react';
import { getMinerFleet } from '../api';

// Live per-miner tracking for curtailments. Foreman gives us hashrate,
// status and last-report age (its API exposes no fan/temp telemetry), so
// "hashrate to zero while reporting" is the shutdown-confirmed signal.
//
// Built for a 5,000+ miner fleet: sites collapsed by default, rows paged
// within a site, one fixed-height scroll container, and polls swap data
// in place (stable keys, no scroll reset, no auto-expand).
//
// When a curtailment goes active a progress modal pops up (once — reopen
// from the header button) walking the staged ramp batch by batch: which
// miners are being slept right now, which are already down, which fire next.

const PAGE = 150;

function fmtHash(ths) {
  if (ths == null) return '—';
  if (ths >= 1000) return `${(ths / 1000).toFixed(2)} PH/s`;
  return `${ths.toFixed(1)} TH/s`;
}

function fmtAge(s) {
  if (s == null) return 'never';
  if (s < 90) return `${s}s ago`;
  if (s < 5400) return `${Math.round(s / 60)}m ago`;
  if (s < 172800) return `${Math.round(s / 3600)}h ago`;
  return `${Math.round(s / 86400)}d ago`;
}

function fmtWhen(iso) {
  try {
    return new Date(iso).toLocaleString(undefined,
      { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  } catch { return iso; }
}

function fmtTime(iso) {
  try {
    return new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit', second: '2-digit' });
  } catch { return iso; }
}

// Which curtailment (if any) covers a site right now. Empty sites = all.
function targetOf(site, entries) {
  const covers = e => !(e.sites || []).length || e.sites.includes(site);
  const active = entries.find(e => e.status === 'active' && covers(e));
  if (active) return { kind: 'active', entry: active };
  const sched = entries.find(e => e.status === 'scheduled' && covers(e));
  if (sched) return { kind: 'scheduled', entry: sched };
  return null;
}

// Ramp progress text for a site's down record on an active entry.
function rampText(site, entry) {
  const down = ((entry.foreman || {}).down || []).find(d => d.site === site);
  if (!down) return null;
  if (down.run_id != null) return 'Foreman ramp';
  const batches = (down.batches || []).filter(b => !b.idle);
  if (!batches.length) return null;
  const sent = batches.filter(b => b.sent_at).length;
  return `step ${sent}/${batches.length}`;
}

// miner id -> its chunk of the staged ramp, so each row can show where it
// sits in the shutdown order ("queued · 1:42 PM" until its batch fires).
function rampBatchMap(site, entry) {
  const down = ((entry.foreman || {}).down || [])
    .find(d => d.site === site && d.method === 'staged-ramp');
  if (!down) return null;
  const map = new Map();
  (down.batches || []).forEach((b, i) => {
    (b.miner_ids || []).forEach(id =>
      map.set(id, { n: i + 1, total: down.batches.length, due: b.due, sent: !!b.sent_at }));
  });
  return map;
}

function minerState(m, target, ramp) {
  if (m.bucket === 'offline') return { key: 'offline', label: 'offline' };
  if (target?.kind === 'active') {
    if (m.bucket !== 'hashing') return { key: 'curtailed', label: 'curtailed ✓' };
    if (ramp && !ramp.sent) {
      return { key: 'queued', label: `queued ${ramp.n}/${ramp.total} · ${fmtTime(ramp.due)}` };
    }
    return { key: 'ramping', label: 'shutting down…' };
  }
  if (m.bucket === 'hashing') return { key: 'hashing', label: 'hashing' };
  return { key: 'down', label: m.status === 'disabled' ? 'disabled' : 'idle' };
}

// The batch-by-batch curtailment walkthrough. Miners render as chips that
// recolor as their batch fires and their hashrate dies.
function ProgressModal({ entry, fleet, onClose }) {
  const targetSites = (entry.sites || []).length
    ? entry.sites : (fleet?.sites || []).map(s => s.site);
  const siteData = (fleet?.sites || []).filter(s => targetSites.includes(s.site));
  const minerById = new Map();
  siteData.forEach(s => s.miners.forEach(m => minerById.set(m.id, m)));
  // Offline/not-reporting units are invisible here — they draw nothing and
  // confirm nothing, so they only muddy the picture. Everything below counts
  // REPORTING miners only.
  const reporting = (id) => {
    const m = minerById.get(id);
    return !!m && m.bucket !== 'offline';
  };
  const total = siteData.reduce((n, s) => n + s.counts.hashing + s.counts.down, 0);
  const hashing = siteData.reduce((n, s) => n + s.counts.hashing, 0);
  const off = total - hashing;
  const pct = total ? Math.round((off / total) * 100) : 0;
  const downs = (entry.foreman || {}).down || [];
  const dry = entry.dry_run;

  const isOff = (id) => minerById.get(id)?.bucket !== 'hashing';

  const chip = (id, sent) => {
    const m = minerById.get(id);
    const st = m.bucket === 'hashing' ? (sent ? 'ramping' : 'queued') : 'curtailed';
    return (
      <span key={id} className={`fpb-miner st-${st}`}
        title={`${m.model || ''} · ${m.ths > 0 ? fmtHash(m.ths) : 'no hash'} · last report ${fmtAge(m.age)}`}>
        {m.ip || id}
      </span>
    );
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ width: 760 }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <h2 style={{ margin: 0 }}>
            Curtailment — {targetSites.join(', ')}
          </h2>
          {dry && <span className="badge warning">DRY RUN — miners untouched</span>}
          {entry.status !== 'active' && <span className="badge muted">{entry.status}</span>}
          <button className="ghost" style={{ marginLeft: 'auto' }} onClick={onClose}>✕</button>
        </div>
        <div className="curtail-subnote" style={{ margin: '2px 0 8px' }}>
          {fmtWhen(entry.start)} → {entry.end ? fmtWhen(entry.end) : 'until resumed'}
          {' · '}green = off · amber = shutting down · dim = waiting its turn
        </div>
        <div className="fp-bar"><div className="fp-fill" style={{ width: `${pct}%` }} /></div>
        <div className="fp-stats" style={{ marginBottom: 8 }}>
          <b>{off}/{total}</b> off ({pct}%) · <b style={{ color: hashing ? 'var(--warning)' : 'var(--ok)' }}>{hashing} still hashing</b>
        </div>
        <div className="fp-modal-scroll">
          {downs.length === 0 && (
            <div className="curtail-subnote">
              No Foreman commands recorded yet — they fire at the start time.
            </div>
          )}
          {downs.map((d, di) => (
            <div key={di} className="fpb-site">
              {downs.length > 1 && <div className="fpb-site-name">{d.site}</div>}
              {d.method === 'staged-ramp' && (() => {
                const all = d.batches || [];
                const ramp = all.filter(b => !b.idle);
                const idle = all.filter(b => b.idle);
                // Only the actively-curtailed (running) miners get individual
                // chips. The idle sweep — thousands of already-idle units —
                // compresses to one progress line.
                const idleIds = idle.flatMap(b => b.miner_ids || []).filter(reporting);
                const idleOff = idleIds.filter(isOff).length;
                const idleSent = idle.filter(b => b.sent_at).length;
                return (
                  <>
                    {ramp.map((b, i) => {
                      const ids = (b.miner_ids || []).filter(reporting);
                      if (!ids.length) return null;
                      const slept = ids.filter(isOff).length;
                      const sentMin = b.sent_at ? (Date.now() - new Date(b.sent_at)) / 60000 : 0;
                      let stateKey, label;
                      if (b.skipped) { stateKey = 'skipped'; label = 'skipped — window ended first'; }
                      else if (!b.sent_at) { stateKey = 'queued'; label = `waiting · sleeps at ${fmtTime(b.due)}`; }
                      else if (slept >= ids.length) { stateKey = 'done'; label = 'off ✓'; }
                      else if (sentMin > 10) {
                        stateKey = 'stuck';
                        label = `⚠ still hashing ${Math.round(sentMin)} min after sleep command — may not support remote sleep`;
                      }
                      else { stateKey = 'sleeping'; label = `shutting down… ${slept}/${ids.length} off`; }
                      return (
                        <div key={i} className={`fpb fpb-${stateKey}`}>
                          <div className="fpb-head">
                            <span className="fpb-n">Batch {i + 1}/{ramp.length}</span>
                            <span>{ids.length} miner{ids.length === 1 ? '' : 's'}</span>
                            <span className={`fpb-state fpb-state-${stateKey}`}>{label}</span>
                            {b.sent_at && <span className="fpb-at">sent {fmtTime(b.sent_at)}</span>}
                          </div>
                          <div className="fpb-miners">{ids.map(id => chip(id, !!b.sent_at))}</div>
                        </div>
                      );
                    })}
                    {idle.length > 0 && idleIds.length > 0 && (
                      <div className={`fpb ${idleOff >= idleIds.length ? 'fpb-done' : idleSent ? 'fpb-sleeping' : 'fpb-queued'}`}>
                        <div className="fpb-head">
                          <span className="fpb-n">Already off</span>
                          <span>
                            {idleIds.length.toLocaleString()} miner{idleIds.length === 1 ? '' : 's'} weren't
                            running — sleep sent anyway so they stay off
                          </span>
                          <span className={`fpb-state ${idleOff >= idleIds.length ? 'fpb-state-done' : 'fpb-state-sleeping'}`}>
                            {idleOff >= idleIds.length
                              ? 'all off ✓'
                              : idleSent < idle.length
                                ? `sleep going out (${idleSent}/${idle.length} groups sent)`
                                : `${(idleIds.length - idleOff).toLocaleString()} started hashing ⚠`}
                          </span>
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
              {d.method !== 'staged-ramp' && (
                <div className="fpb">
                  <div className="fpb-head">
                    <span className="fpb-n">{d.site}</span>
                    <span className="fpb-state">
                      {d.run_id != null ? `Foreman native ramp (run ${d.run_id})`
                        : d.skipped || d.error || 'commands sent'}
                    </span>
                  </div>
                </div>
              )}
            </div>
          ))}
          {((entry.foreman || {}).resume || []).length > 0 && (
            <div className="fpb-site">
              <div className="fpb-site-name">Wake-up</div>
              {(entry.foreman.resume || []).map((r, i) => (
                <div key={i} className={`fpb ${r.failed ? 'fpb-sleeping' : 'fpb-done'}`}>
                  <div className="fpb-head">
                    <span className="fpb-n">{r.site || 'all'}</span>
                    <span className={`fpb-state ${r.failed ? 'fpb-state-sleeping' : 'fpb-state-done'}`}>
                      {r.method === 'ramp-revert'
                        ? 'Foreman ramp reverted'
                        : `wake sent to ${(r.miner_ids || []).length.toLocaleString()} miners`}
                      {r.ok != null ? ` · ${r.ok} ok` : ''}
                      {r.failed ? ` · ${r.failed} FAILED` : ''}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function MinerFleet({ entries }) {
  const [fleet, setFleet] = useState(null);
  const [err, setErr] = useState(null);
  const [expanded, setExpanded] = useState(() => new Set());
  const [shown, setShown] = useState({});      // site -> row cap
  const [filter, setFilter] = useState('');
  const [bucket, setBucket] = useState('all'); // all | hashing | down | offline
  const [progressId, setProgressId] = useState(null);
  const autoOpenedRef = useRef(new Set());

  const activeEntries = useMemo(() => entries.filter(e => e.status === 'active'), [entries]);
  const anyActive = activeEntries.length > 0;

  // Pop the progress modal once when a curtailment goes active; after that
  // it reopens from the header button only.
  useEffect(() => {
    for (const e of activeEntries) {
      if (!autoOpenedRef.current.has(e.id)) {
        autoOpenedRef.current.add(e.id);
        setProgressId(e.id);
        break;
      }
    }
  }, [activeEntries]);
  const progressEntry = entries.find(e => e.id === progressId);

  // Poll faster while a curtailment window is live — that's when you're
  // watching miners wind down. Skips ticks while the tab is hidden.
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      if (document.hidden) return;
      try {
        const d = await getMinerFleet();
        if (cancelled) return;
        if (d?.ok) { setFleet(d); setErr(null); }
        else setErr(d?.error || 'fleet fetch failed');
      } catch (e) {
        if (!cancelled) setErr(String(e?.message || e));
      }
    };
    tick();
    const t = setInterval(tick, anyActive ? 20000 : 60000);
    return () => { cancelled = true; clearInterval(t); };
  }, [anyActive]);

  const toggleSite = (s) => setExpanded(prev => {
    const next = new Set(prev);
    if (next.has(s)) next.delete(s); else next.add(s);
    return next;
  });

  // Targeted sites float to the top (active first, then scheduled). Order
  // only changes when a curtailment starts/ends, not on data polls.
  const sites = useMemo(() => {
    if (!fleet?.sites) return [];
    const rank = s => {
      const t = targetOf(s.site, entries);
      return t?.kind === 'active' ? 0 : t?.kind === 'scheduled' ? 1 : 2;
    };
    return [...fleet.sites].sort((a, b) => rank(a) - rank(b) || a.site.localeCompare(b.site));
  }, [fleet, entries]);

  const q = filter.trim().toLowerCase();
  const filtering = q !== '' || bucket !== 'all';
  const matchMiner = (m) =>
    (!q || `${m.ip || ''} ${m.model || ''} ${m.group || ''} ${m.rack || ''}`.toLowerCase().includes(q))
    && (bucket === 'all' || m.bucket === bucket);

  if (!fleet && !err) return null; // nothing useful to show yet

  const t = fleet?.totals;
  return (
    <div className="panel">
      <div className="curtail-head">
        <h2 style={{ margin: 0 }}>Miner fleet</h2>
        <span className="curtail-subnote">
          live from Foreman — a miner counts as off once it stops hashing
        </span>
        {anyActive && (
          <button className="primary" style={{ padding: '3px 10px', fontSize: 12 }}
            onClick={() => setProgressId(activeEntries[0].id)}>
            ⚡ curtailment progress
          </button>
        )}
        {fleet?.at && <span className="curtail-subnote">updated {fmtTime(fleet.at)}</span>}
      </div>

      {err && <div className="curtail-error">Fleet unavailable: {err}</div>}
      {fleet?.warming && (
        <div className="curtail-subnote" style={{ padding: '8px 0' }}>
          Building the miner roster from Foreman (takes ~30 s after a backend restart)…
        </div>
      )}

      {t && !fleet.warming && (
        <div className="fleet-strip">
          <span className="fleet-stat"><b>{t.miners.toLocaleString()}</b> miners</span>
          <span className="fleet-stat ok"><b>{t.hashing.toLocaleString()}</b> hashing</span>
          <span className="fleet-stat dim"><b>{t.down.toLocaleString()}</b> idle</span>
          <span className="fleet-stat bad"><b>{t.offline.toLocaleString()}</b> offline</span>
          <span className="fleet-stat"><b>{fmtHash(t.ths)}</b> fleet total</span>
          <input type="text" placeholder="Filter miners… (ip, model, rack)" value={filter}
            onChange={e => setFilter(e.target.value)} style={{ marginLeft: 'auto' }} />
          <div className="toggle-group">
            {[['all', 'all'], ['hashing', 'hashing'], ['down', 'idle'], ['offline', 'offline']].map(([b, label]) => (
              <button key={b} className={bucket === b ? 'active' : ''}
                onClick={() => setBucket(b)}>{label}</button>
            ))}
          </div>
        </div>
      )}

      {sites.length > 0 && (
        <div className="fleet-scroll">
          {sites.map(s => {
            const target = targetOf(s.site, entries);
            // Filtering (text or status chip) hides sites with no matches and
            // opens the rest; otherwise sites open only by click.
            const matches = filtering ? s.miners.filter(matchMiner) : s.miners;
            if (filtering && matches.length === 0) return null;
            const open = expanded.has(s.site) || filtering;
            const visible = open ? matches : [];
            const cap = shown[s.site] || PAGE;
            const rows = visible.slice(0, cap);
            const curtailed = target?.kind === 'active'
              ? s.miners.filter(m => m.bucket === 'down').length : 0;
            const batchMap = target?.kind === 'active' ? rampBatchMap(s.site, target.entry) : null;
            return (
              <div key={s.site} className={`fleet-site ${target ? `target-${target.kind}` : ''}`}>
                <button type="button" className="fleet-site-head" onClick={() => toggleSite(s.site)}>
                  <span className="loc-group-caret">{open ? '▾' : '▸'}</span>
                  <span className="fleet-site-name">{s.site}</span>
                  {target?.kind === 'active' && (
                    <span className="fleet-badge live">
                      CURTAILING · {curtailed}/{s.miners.length} down
                      {rampText(s.site, target.entry) ? ` · ${rampText(s.site, target.entry)}` : ''}
                      {target.entry.dry_run ? ' · dry' : ''}
                    </span>
                  )}
                  {target?.kind === 'scheduled' && (
                    <span className="fleet-badge sched">curtail {fmtWhen(target.entry.start)}</span>
                  )}
                  <span className="fleet-site-counts">
                    <span className="ok">{s.counts.hashing}⛏</span>
                    {s.counts.down > 0 && <span className="dim"> · {s.counts.down} idle</span>}
                    {s.counts.offline > 0 && <span className="bad"> · {s.counts.offline} off</span>}
                  </span>
                  <span className="fleet-site-hash">{fmtHash(s.ths)}</span>
                </button>
                {open && (
                  <div className="fleet-rows">
                    {rows.map(m => {
                      const st = minerState(m, target, batchMap?.get(m.id));
                      return (
                        <div key={m.id} className={`fleet-row st-${st.key}`}
                          title={`${m.model || ''} · rack ${m.rack || '—'} · status ${m.status || '—'} · last report ${fmtAge(m.age)}`}>
                          <span className={`fleet-dot st-${st.key}`} />
                          <span className="fleet-ip">{m.ip || m.id}</span>
                          <span className="fleet-model">{m.model || '—'}</span>
                          <span className="fleet-rack">{m.group || m.rack || ''}</span>
                          <span className="fleet-hash">{m.ths > 0 ? fmtHash(m.ths) : '—'}</span>
                          <span className={`fleet-state st-${st.key}`}>{st.label}</span>
                          <span className="fleet-age">{fmtAge(m.age)}</span>
                        </div>
                      );
                    })}
                    {visible.length > cap && (
                      <button className="ghost fleet-more"
                        onClick={() => setShown(prev => ({ ...prev, [s.site]: cap + 500 }))}>
                        Show 500 more ({(visible.length - cap).toLocaleString()} hidden)
                      </button>
                    )}
                    {open && visible.length === 0 && (
                      <div className="curtail-subnote" style={{ padding: '4px 10px' }}>
                        no miners match the filter
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {progressEntry && (
        <ProgressModal entry={progressEntry} fleet={fleet} onClose={() => setProgressId(null)} />
      )}
    </div>
  );
}
